/**
 * Create routes from a lasso selection (Phase 3 of the lasso plan).
 *
 * A lasso can select thousands of doors. One route with 5,000 stops is
 * nonsense — a route is a walking shift (~120 doors), and est_hours would
 * cheerfully claim ~60 hours for one canvasser. So the selection is split into
 * shift-sized routes, proposed here, and adjustable BEFORE anything is written.
 *
 * The pre-commit adjustment is not a nicety. Routes are effectively write-once:
 * PATCH only touches name/status/google_maps_url, stops cannot be reordered,
 * added, or removed, and there is no DELETE endpoint. Archiving is the only
 * retreat. This modal is the last point at which a mistake is cheap, which is
 * why every derived consequence (turf, hours, door counts, the truncation) is
 * shown here rather than discovered afterward in the routes list.
 *
 * Styling follows the admin drawer idiom (semantic tokens, right-side panel).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import {
  previewRouteSplit,
  commitRouteSplit,
  type RouteSplitPreviewResponse,
} from '@/api/routes';

/** Mirror the server's caps; used to explain, never to gate — the server decides. */
const MAX_SPLIT_VOTERS = 5000;
const MAX_ROUTES_PER_SPLIT = 40;

type Props = {
  /** Ids from the lasso — already capped by the server at 5000. */
  voterIds: number[];
  /** The honest server-side count inside the polygon, which may exceed voterIds.length. */
  totalSelected: number;
  /** True when the server truncated voterIds relative to totalSelected. */
  truncated: boolean;
  onClose: () => void;
  onCreated: (count: number) => void;
};

function extractError(err: unknown, fallback: string): string {
  const resp = (err as any)?.response;
  if (resp?.status === 403) {
    return (
      resp?.data?.detail ||
      'You do not have permission to create routes. This requires a coordinator or above.'
    );
  }
  return resp?.data?.detail || (err as any)?.message || fallback;
}

function isAbortError(err: any): boolean {
  return (
    err?.name === 'CanceledError' ||
    err?.name === 'AbortError' ||
    err?.code === 'ERR_CANCELED'
  );
}

export default function CreateRouteModal({
  voterIds,
  totalSelected,
  truncated,
  onClose,
  onCreated,
}: Props) {
  // Matches the server default. A route in this product is a block, not a
  // shift — the real ones average 11 doors / 0.9h.
  const [maxDoors, setMaxDoors] = useState(12);
  const [namePrefix, setNamePrefix] = useState('Route');
  const [preview, setPreview] = useState<RouteSplitPreviewResponse | null>(null);
  const [names, setNames] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inputCls =
    'w-full rounded-md border border-border-subtle bg-bg-base px-3 py-2 text-sm text-fg-primary placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-accent';

  // Re-preview on knob changes, debounced — dragging the door count would
  // otherwise fire a request per keystroke.
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    const t = setTimeout(() => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      setError(null);
      previewRouteSplit(
        { voter_ids: voterIds, max_doors_per_route: maxDoors, name_prefix: namePrefix },
        ctrl.signal,
      )
        .then((data) => {
          setPreview(data);
          // Server-suggested names are the starting point; the coordinator's
          // edits live in `names` and are what actually get committed.
          setNames(data.groups.map((g) => g.name));
          setLoading(false);
        })
        .catch((err) => {
          if (isAbortError(err)) return;
          setPreview(null);
          setError(extractError(err, 'Could not work out how to split this selection.'));
          setLoading(false);
        });
    }, 300);
    return () => clearTimeout(t);
  }, [voterIds, maxDoors, namePrefix]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const handleCommit = useCallback(async () => {
    if (!preview) return;
    setCommitting(true);
    setError(null);
    try {
      // {name, voter_ids} only — the commit model is extra="forbid", so posting
      // a preview group back verbatim is a 422.
      const created = await commitRouteSplit(
        preview.groups.map((g, i) => ({
          name: (names[i] ?? g.name).trim(),
          voter_ids: g.voter_ids,
        })),
      );
      onCreated(created.length);
    } catch (err) {
      setError(extractError(err, 'Could not create the routes. Nothing was written.'));
      setCommitting(false);
    }
  }, [preview, names, onCreated]);

  const nullTurfCount = useMemo(
    () => (preview?.groups ?? []).filter((g) => !g.subregion).length,
    [preview],
  );
  const blankName = names.some((n) => !n.trim());

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className="relative z-10 flex h-full w-full max-w-lg flex-col bg-bg-raised shadow-xl">
        <div className="flex items-center justify-between border-b border-border-subtle px-5 py-4">
          <h2 className="text-base font-semibold text-fg-primary">Create routes from selection</h2>
          <button
            onClick={onClose}
            className="rounded p-1 text-fg-muted hover:bg-bg-base hover:text-fg-primary"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
          {truncated && (
            <div className="flex gap-2 rounded-md border border-status-warning/40 bg-status-warning/10 px-3 py-2 text-xs text-fg-primary">
              <AlertTriangle size={14} className="mt-0.5 shrink-0 text-status-warning" />
              <span>
                Your lasso caught{' '}
                <strong>{totalSelected.toLocaleString()}</strong> doors. Only the first{' '}
                <strong>{MAX_SPLIT_VOTERS.toLocaleString()}</strong> were sent, so{' '}
                <strong>{(totalSelected - MAX_SPLIT_VOTERS).toLocaleString()}</strong> are{' '}
                <strong>not</strong> included below. In practice the {MAX_ROUTES_PER_SPLIT}-route
                limit stops you sooner — about{' '}
                <strong>{(MAX_ROUTES_PER_SPLIT * maxDoors).toLocaleString()}</strong> doors at{' '}
                {maxDoors} per route. Lasso a smaller area.
              </span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                htmlFor="split-max-doors"
                className="mb-1 block text-xs font-semibold text-fg-secondary"
              >
                Doors per route
              </label>
              <input
                id="split-max-doors"
                type="number"
                min={10}
                max={500}
                value={maxDoors}
                onChange={(e) => setMaxDoors(Number(e.target.value))}
                className={inputCls}
              />
            </div>
            <div>
              <label
                htmlFor="split-prefix"
                className="mb-1 block text-xs font-semibold text-fg-secondary"
              >
                Name prefix
              </label>
              <input
                id="split-prefix"
                type="text"
                value={namePrefix}
                onChange={(e) => setNamePrefix(e.target.value)}
                className={inputCls}
              />
            </div>
          </div>

          {loading && (
            <div className="flex items-center gap-2 py-6 text-sm text-fg-muted">
              <Loader2 size={16} className="animate-spin" />
              Working out the split…
            </div>
          )}

          {error && (
            <div className="rounded-md border border-status-danger/40 bg-status-danger/10 px-3 py-2 text-xs text-status-danger">
              {error}
            </div>
          )}

          {preview && !loading && (
            <>
              <div className="text-sm text-fg-secondary">
                <strong className="text-fg-primary">{preview.route_count}</strong>{' '}
                {preview.route_count === 1 ? 'route' : 'routes'} from{' '}
                <strong className="text-fg-primary">
                  {preview.total_doors.toLocaleString()}
                </strong>{' '}
                doors. Nothing is created until you confirm.
              </div>

              {nullTurfCount > 0 && (
                <div className="flex gap-2 rounded-md border border-status-warning/40 bg-status-warning/10 px-3 py-2 text-xs text-fg-primary">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0 text-status-warning" />
                  <span>
                    {nullTurfCount === 1 ? '1 route has' : `${nullTurfCount} routes have`} no turf.
                    {' '}Routes without a turf do not show up in the Walking Routes view — they
                    would be created, then be hard to find.
                  </span>
                </div>
              )}

              <ul className="flex flex-col gap-2">
                {preview.groups.map((g, i) => (
                  <li
                    key={i}
                    className="rounded-md border border-border-subtle bg-bg-base px-3 py-2"
                  >
                    <input
                      type="text"
                      value={names[i] ?? ''}
                      aria-label={`Name for route ${i + 1}`}
                      onChange={(e) => {
                        const next = [...names];
                        next[i] = e.target.value;
                        setNames(next);
                      }}
                      className="w-full bg-transparent text-sm font-medium text-fg-primary focus:outline-none focus:ring-1 focus:ring-accent rounded px-1 -mx-1"
                    />
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-muted">
                      <span>{g.door_count} doors</span>
                      <span>~{g.est_hours.toFixed(1)}h</span>
                      {g.subregion ? (
                        <span>turf {g.subregion}</span>
                      ) : (
                        <span className="text-status-warning">no turf</span>
                      )}
                      {g.precinct_num && <span>P{g.precinct_num}</span>}
                    </div>
                  </li>
                ))}
              </ul>

              <p className="text-xs text-fg-muted">
                Hours are estimated from door counts; the saved route may differ once real
                walking distances are applied. Which doors land in which route will not change.
              </p>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border-subtle px-5 py-4">
          <button
            onClick={onClose}
            className="rounded-md px-3 py-2 text-sm text-fg-secondary hover:bg-bg-base"
          >
            Cancel
          </button>
          <button
            onClick={handleCommit}
            disabled={!preview || loading || committing || blankName}
            className="inline-flex items-center gap-2 rounded-md bg-accent px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {committing && <Loader2 size={14} className="animate-spin" />}
            {committing
              ? 'Creating…'
              : preview
                ? `Create ${preview.route_count} ${
                    preview.route_count === 1 ? 'route' : 'routes'
                  }`
                : 'Create routes'}
          </button>
        </div>
      </div>
    </div>
  );
}
